import { Outlet } from "react-router-dom";
import {
  Box,
  Flex,
  Paper,
  Image,
  rem,
  useMantineColorScheme,
  Group,
  ActionIcon,
  Text,
  useMantineTheme,
  ScrollArea,
  Stack,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { useRef } from "react";
import Autoplay from "embla-carousel-autoplay";
import { MoonIcon, SunIcon, EnvelopeIcon } from "@phosphor-icons/react";


const images = [
  "/assets/carousel-image1.jpg",
  "/assets/carousel-image2.jpg",
  "/assets/carousel-image3.jpg",
];

const carouselTexts = [
  "With OneSelf Path®, your health care is pointed in the right direction.",
  "OneSelf Path® empowers you to confidently navigate your health care decisions.",
  "Get started on your OneSelf Path® to better health.",
];

// Extracted common components
const ThemeToggle = ({ colorScheme, toggleColorScheme }: { colorScheme: string; toggleColorScheme: () => void }) => (
  <Group 
    justify="flex-end" 
    p="xs" 
    mr="xs"
    style={{
      marginTop: "0px", 
      marginBottom: "0px",
      paddingTop: "8px",  
      paddingBottom: "8px"
    }}
  >
    <ActionIcon onClick={toggleColorScheme} size="md">
      {colorScheme === "dark" ? <SunIcon /> : <MoonIcon />}
    </ActionIcon>
  </Group>
);

const Logo = ({ colorScheme }: { colorScheme: string }) => (
  <Image
    src={
      colorScheme === "dark"
        ? "/assets/white_logo.png"
        : "/assets/logo.png"
    }
    w={174}
    h={102}
    mb="xl"
    fit="contain"
    // style={{
    //   marginTop: "32px",
    //   marginBottom: "0px",
    //   marginLeft: "0px",
    //   marginRight: "0px"
    // }}
  />
);

const Footer = ({ colorScheme, theme, isMobile = false }: { colorScheme: string; theme: any; isMobile?: boolean }) => (
  <Box mt={"xl"}>
    <Group 
      gap={isMobile ? "sm" : 280} 
      justify={isMobile ? "center" : "space-between"} 
      w="100%" 
      style={isMobile ? { flexDirection: "column" } : {}}
    >
      <Text
        size={isMobile ? "sm" : "md"}
        c={
          colorScheme === "dark"
            ? "theme.colors.neutral[2]"
            : "theme.colors.neutral[9]"
        }
      >
        © OneSelf Path 2025
      </Text>
      <Group gap={0}>
        <EnvelopeIcon
          size={isMobile ? 16 : 20}
          color={
            colorScheme === "dark"
              ? theme.colors.indigo[6]
              : theme.colors.indigo[9]
          }
        />
        <a
          href="mailto:info@oneselftechnology.com"
        >
          info@oneselftechnology.com
        </a>
      </Group>
    </Group>
  </Box>
);

const ImageCarousel = ({ isMobile = false }: { isMobile?: boolean }) => {
  const autoplay = useRef(Autoplay({ delay: 3000 }));
  
  return (
    <Carousel
      plugins={[autoplay.current]}
      withIndicators
      withControls
      styles={{
        indicator: {
          width: rem(isMobile ? 12 : 15),
          height: rem(isMobile ? 12 : 15),
          borderRadius: rem(1000),
          backgroundColor: "#FFFFFF",
          transition: "width 250ms ease, height 250ms ease, opacity 250ms ease, background-color 250ms ease",
          opacity: 1,
        },
        indicators: {
          bottom: rem(isMobile ? 16 : 20),
          left: "50%",
          transform: "translateX(-50%)",
        },
      }}
    >
      {images?.map((url, index) => (
        <Carousel.Slide key={url}>
          <Box style={{ position: "relative", width: "100%", height: "100%" }}>
            <Image src={url} h={isMobile ? "400px" : "100vh"} fit="cover" />
            <Box
              style={{
                position: "absolute",
                bottom: rem(isMobile ? 50 : 60),
                left: rem(isMobile ? 20 : 24),
                width: "85%",
                maxWidth: rem(500),
                zIndex: 10,
              }}
            >
              <Text
                fw={500}
                c="white"
                style={{
                  fontSize: rem(isMobile ? 16 : 28),
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                  lineHeight: "120%",
                }}
              >
                {carouselTexts[index]}
              </Text>
            </Box>
          </Box>
        </Carousel.Slide>
      ))}
    </Carousel>
  );
};

export default function AuthLayout() {
  const theme = useMantineTheme();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  
  // Card background color - gray for normal mode, dark for dark mode
  const cardBg = colorScheme === "dark" 
    ? theme.colors.neutral[8] 
    : "rgb(244, 244, 244)";
  
  // Outer container background for dark mode
  const containerBg = colorScheme === "dark"
    ? theme.colors.neutral[9]
    : theme.colors.neutral[1];

  return (
    <Box w="100vw" h="100vh"  style={{ overflow: "hidden" }}>
      {/* Mobile/Tablet Layout */}
      <Box
        h="100vh"
        style={{ overflow: "auto" }}
        display={{ base: "block", lg: "none" }}
      >
        <Stack gap="xs" p="xs">
          <Box
            style={{ 
              borderRadius: "20px", 
              overflow: "hidden",
              minHeight: "auto"
            }}
            bg={containerBg}
          >
            <ThemeToggle colorScheme={colorScheme} toggleColorScheme={toggleColorScheme} />
            
            <Box style={{ padding: "0 20px 20px 20px" }}>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  margin: "0 auto",
                  padding: "20px 0",
                }}
              >
                <Logo colorScheme={colorScheme} />
                {/* Only the form content is inside the Paper card */}
                <Paper
                  style={{ 
                    borderRadius: "20px", 
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "628px",
                    minWidth: "280px",
                  }}
                  bg={cardBg}
                  p="calc(1.5625rem * var(--mantine-scale))"
                >
                  <Outlet />
                </Paper>
                <Footer colorScheme={colorScheme} theme={theme} isMobile={true} />
              </Box>
            </Box>
          </Box>

          <Paper
            style={{ 
              borderRadius: "20px",
              height: "400px",
              marginTop: "0px" 
            }}
          >
            <Box w="100%" h="100%">
              <ImageCarousel isMobile={true} />
            </Box>
          </Paper>
        </Stack>
      </Box>

      {/* Desktop Layout */}
      <Flex
        w="100%"
        h="100%"
        justify="space-between"
        gap="xs"
        p="xs"
        display={{ base: "none", lg: "flex" }}
      >
        <Box
          flex={1}
          style={{ 
            borderRadius: "20px", 
            overflow: "hidden",
            marginRight: "0px"  // Remove margin for tight alignment
          }}
          bg={containerBg}
        >
          <ThemeToggle colorScheme={colorScheme} toggleColorScheme={toggleColorScheme} />
          
          <ScrollArea h="calc(100vh - 80px)" style={{ padding: "0 20px" }}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                margin: "0 auto",
                minHeight: "100%",
                padding: "20px 0",
              }}
            >
              <Logo colorScheme={colorScheme} />
              {/* Only the form content is inside the Paper card */}
              <Paper
                style={{ 
                  borderRadius: "20px", 
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "628px",
                  minWidth: "280px",
                }}
                bg={cardBg}
                p="calc(1.5625rem * var(--mantine-scale))"
              >
                <Outlet />
              </Paper>
              <Footer colorScheme={colorScheme} theme={theme} />
            </Box>
          </ScrollArea>
        </Box>
        
        <Paper
          flex={1}
          style={{ borderRadius: "20px" }}
        >
          <Box w="100%" h="100%">
            <ImageCarousel />
          </Box>
        </Paper>
      </Flex>
    </Box>
  );
}
